<template>
  <table class="pd-documents-table">
    <tbody>
      <tr v-if="product.documents?.length">
        <td>
          <strong>{{ $t('product.moreProductDetails') }}</strong>
        </td>
        <td>
          <fn-link
            v-for="document in product.documents"
            :key="document.url"
            no-arrow
            inline
            icon
            :href="document.url"
            target="_blank"
          >
            <fn-icon
              icon="pdf"
              color="black"
              size="xs"
            />
            {{ document.altText || document.description }}
          </fn-link>
        </td>
      </tr>

      <tr v-if="product.distributingCompany">
        <td>
          <strong>{{ $t('product.distributingCompany') }}</strong>
        </td>
        <td>
          {{ product.distributingCompany }}
        </td>
      </tr>

      <tr v-if="product.producerNumber">
        <td>
          <strong>{{ $t('product.producerNumber') }}</strong>
        </td>
        <td>
          {{ product.producerNumber }}
        </td>
      </tr>

      <tr v-if="product.organicControlNumber">
        <td>
          <strong>{{ $t('product.organicControlNumber') }}</strong>
        </td>
        <td>
          {{ product.organicControlNumber }}
        </td>
      </tr>

      <template
        v-for="(item, index) in manufacturer"
        :key="item.key"
      >
        <tr
          v-if="manufacturer.length > 1"
          class="pddt-row--inline-heading"
        >
          <td colspan="2">{{ item.product_code }} {{ item.product_name }}</td>
        </tr>

        <tr v-if="manufacturerUrls[index]?.lineManufacturer && manufacturerUrls[index]?.urlManufacturer">
          <td>
            <strong>{{ $t('product.manufacturer') }}</strong>
          </td>
          <td>
            {{ manufacturerUrls[index].lineManufacturer }}
            <a
              :href="manufacturerUrls[index].urlManufacturer"
              target="_blank"
            >
              {{ item.manufacturer_contact }}
            </a>
          </td>
        </tr>

        <tr v-if="manufacturerUrls[index]?.lineResponsiblePerson && manufacturerUrls[index]?.urlResponsiblePerson">
          <td>
            <strong>{{ $t('product.responsiblePerson') }}</strong>
          </td>
          <td>
            {{ manufacturerUrls[index].lineResponsiblePerson }}
            <a :href="manufacturerUrls[index].urlResponsiblePerson">
              {{ item.responsible_person_contact }}
            </a>
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<script setup lang="ts">
const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
});

const addUrlOrMailtoProtocol = (value: string) => {
  if (!value) {
    return undefined;
  }

  // Simple check should be enough, extensive checks using regexes would be overkill here until proven wrong
  if (value.includes('@')) {
    return `mailto:${value}`;
  }

  return value.startsWith('https') ? value : `https://${value}`;
};

const manufacturer = computed(() =>
  Array.isArray(props.product.manufacturerData) ? props.product.manufacturerData : [],
);

const manufacturerUrls = computed(() => {
  if (!props.product.manufacturerData) {
    return [];
  }

  return props.product.manufacturerData.map((item: any) => {
    const contactManufacturer = item.manufacturer_contact;
    const contactPerson = item.responsible_person_contact;

    // Handle string parts delimiters
    const lineManufacturer = [item.name, item.manufacturer_address].filter(Boolean).join(', ');
    const linePerson = [item.responsible_person_company_name, item.responsible_person_address]
      .filter(Boolean)
      .join(', ');

    // Handle delimiters between string parts and contact URL
    const lastSeparatorManufacturer = lineManufacturer && contactManufacturer && `, `;
    const lastSeparatorPerson = linePerson && contactPerson && `, `;

    return {
      lineManufacturer: lineManufacturer + lastSeparatorManufacturer,
      lineResponsiblePerson: linePerson + lastSeparatorPerson,
      urlManufacturer: addUrlOrMailtoProtocol(contactManufacturer),
      urlResponsiblePerson: addUrlOrMailtoProtocol(contactPerson),
      key: `${item.product_code}${item.product_name}`,
    };
  });
});
</script>

<style lang="scss" scoped>
.pd-documents-table {
  td {
    display: block;

    @include bp(m) {
      display: table-cell;
      padding-top: 5px;
      padding-bottom: 5px;
    }

    &:last-child {
      padding: 5px 0 $pad 0;

      @include bp(m) {
        padding: 0 0 0 $space;
      }
    }
  }

  .pddt-row--inline-heading {
    td {
      padding: $space 0 5px 0;
    }
  }
}
</style>
